import { useState } from "react";
import LineupSlots from "./LineupSlots";
import AlternatePicks from "./AlternatePicks";
import FighterSelectionModal from "./FighterSelectionModal";
import SubmissionModal from "./SubmissionModal";
import RemoveAllConfirmationModal from "./RemoveAllConfirmationModal";
import GlobalLineupSyncModal from "./GlobalLineupSyncModal";
import useLineupSubmission from "../../hooks/lineup/useLineupSubmission";
import Loading from "../common/Loading";
import CountdownTimer from "../common/CountdownTimer";

/**
 * Main container component for the lineup submission flow
 * Single page layout with lineup and alternates sections
 *
 * @param {Object} props
 * @param {string} props.eventId - ID of the event
 * @param {string} [props.leagueId] - Optional league ID (if not provided, uses Redux state)
 * @param {string} [props.userId] - Optional user ID (if not provided, uses Redux state)
 * @param {Array|Object} props.fighters - Array of all available fighters or full event data
 * @param {string} props.eventStart - Date/time when event starts
 * @param {Function} [props.onSubmissionSuccess] - Callback fired after successful submission
 * @returns {JSX.Element}
 */
const LineupSubmission = ({
  eventId,
  leagueId,
  userId,
  fighters,
  eventStart,
  onSubmissionSuccess,
}) => {
  // Selection modal state
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [isAlternatePick, setIsAlternatePick] = useState(false);

  // State to control editing mode
  const [isEditMode, setIsEditMode] = useState(false);

  // State for remove all confirmation modal
  const [showRemoveAllConfirmation, setShowRemoveAllConfirmation] =
    useState(false);

  // State for swap confirmation modal
  const [swapConfirmation, setSwapConfirmation] = useState(null);
  // Shape: { existingFighter, newFighter, targetSlot } or null

  // Custom hook for lineup submission logic
  const {
    // State
    lineup,
    lotn,
    alternates,
    alternateLotn,
    projectedTotal,
    showSubmissionModal,
    showGlobalSyncModal,
    isGlobalSyncUpdate,
    isLoading,
    error,
    isComplete,
    hasSubmission,

    // Actions
    addFighter,
    moveFighter,
    removeFighter,
    toggleLotn,
    addAlternate,
    removeAlternate,
    toggleAlternateLotn,
    removeAll,
    handleSubmitButtonClick,
    handleConfirmSubmit,
    setShowSubmissionModal,
    isFighterOrOpponentInLineup,
    handleGlobalSyncConfirm,
    handleGlobalSyncDecline,
  } = useLineupSubmission({ eventId, leagueId, userId, onSubmissionSuccess });

  // Add a derived state to check if there's an actual lineup loaded
  const hasLoadedLineup = lineup.some((fighter) => fighter !== null);

  // Check if we're waiting for data to load into Redux
  const isWaitingForData = hasSubmission && !hasLoadedLineup && isLoading;

  // Is event started check
  const isEventStarted = new Date(eventStart) <= new Date();

  // Modal handlers
  const handleOpenSelection = (position, isAlternate = false) => {
    setCurrentPosition(position);
    setIsAlternatePick(isAlternate);
    setShowSelectionModal(true);
  };

  const handleSelectFighter = (fighter, slotOverride) => {
    if (isAlternatePick) {
      addAlternate(fighter);
      setShowSelectionModal(false);
    } else {
      const targetSlot =
        slotOverride !== undefined ? slotOverride : currentPosition;
      const existingFighter = lineup[targetSlot];

      if (existingFighter && existingFighter.FighterId !== fighter.FighterId) {
        // Slot is occupied by a different fighter — show swap confirmation
        setSwapConfirmation({
          existingFighter,
          newFighter: fighter,
          targetSlot,
        });
        setShowSelectionModal(false);
      } else {
        // Slot is empty or same fighter — just place
        addFighter(targetSlot, fighter);
        setShowSelectionModal(false);
      }
    }
  };

  // Swap confirmation handlers
  const handleConfirmSwap = () => {
    if (swapConfirmation) {
      addFighter(swapConfirmation.targetSlot, swapConfirmation.newFighter);
      setSwapConfirmation(null);
    }
  };

  const handleCancelSwap = () => {
    setSwapConfirmation(null);
  };

  // Remove all confirmation handlers
  const handleRemoveAllClick = () => {
    setShowRemoveAllConfirmation(true);
  };

  const handleConfirmRemoveAll = () => {
    removeAll();
    setShowRemoveAllConfirmation(false);
  };

  const handleCancelRemoveAll = () => {
    setShowRemoveAllConfirmation(false);
  };

  // Get the proper event data structure to pass to the selection modal
  const getEventDataForModal = () => {
    if (fighters && typeof fighters === "object" && fighters.Fights) {
      return fighters;
    }

    return {
      Fights: fighters ? [{ Fighters: fighters, WeightClass: "Mixed" }] : [],
    };
  };

  const getFlatFightersArray = () => {
    if (fighters && typeof fighters === "object" && fighters.Fights) {
      return fighters.Fights.filter(
        (fight) => !fight.hidden && !fight.cancelled,
      ).flatMap((fight) => fight.Fighters || []);
    }
    return fighters;
  };

  // Function to get weight class for a fighter from the event data
  const getWeightClassForFighter = (fighterId) => {
    // First, check if any fighter in lineup or alternates has this ID and already has WeightClass
    const allLineupFighters = [...lineup.filter((f) => f), ...alternates];
    const existingFighter = allLineupFighters.find(
      (f) => f.FighterId === fighterId,
    );
    if (
      existingFighter &&
      existingFighter.WeightClass &&
      existingFighter.WeightClass !== "Unknown"
    ) {
      return existingFighter.WeightClass;
    }

    // If we have full event data with Fights array
    if (fighters && typeof fighters === "object" && fighters.Fights) {
      for (const fight of fighters.Fights) {
        if (fight.Fighters) {
          const fighter = fight.Fighters.find((f) => f.FighterId === fighterId);
          if (fighter) {
            return fight.WeightClass || "Unknown";
          }
        }
      }
    }
    // If we have flat array with weight class info embedded
    else if (Array.isArray(fighters)) {
      const fighter = fighters.find((f) => f.FighterId === fighterId);
      return fighter?.WeightClass || "Unknown";
    }

    return "Unknown";
  };

  // Handle toggling edit mode
  const handleEditLineup = () => {
    setIsEditMode(true);
  };

  if (isLoading) {
    return <Loading fullScreen={false} />;
  }

  if (error) {
    return (
      <div className="text-red-500 p-4">
        Error loading lineup data: {error.message}
      </div>
    );
  }

  if (isEventStarted) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <h2 className="text-xl font-bold text-gray-800 mb-4">
          Event has started
        </h2>
        <p className="text-gray-600">
          The event has already started. Lineups can no longer be modified.
        </p>
      </div>
    );
  }

  // Show loading while data is being synced to Redux
  if (isWaitingForData) {
    return <Loading fullScreen={false} />;
  }

  // Render submission summary and edit button when a submission exists and data is loaded
  if (hasSubmission && !isEditMode && hasLoadedLineup) {
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4">
            {/* Projected Score - moved to top */}
            <div className="bg-gradient-to-r from-deep-red/5 to-bright-red/5 rounded-lg p-3 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-deep-red">
                  {projectedTotal?.toFixed(1) || "0.0"}
                </div>
                <div className="text-xs text-medium-grey">Projected Points</div>
              </div>
            </div>

            {/* Primary Lineup Summary */}
            <div className="mb-4">
              <h3 className="font-bold text-sm text-dark-grey mb-2">
                Primary Lineup
              </h3>
              <div className="space-y-2">
                {lineup.map(
                  (fighter, index) =>
                    fighter && (
                      <div
                        key={index}
                        className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-medium-grey bg-gray-200 w-6 h-6 rounded-full flex items-center justify-center font-bold">
                            {index + 1}
                          </span>
                          <span className="font-medium text-sm text-dark-grey">
                            {fighter.FirstName} {fighter.LastName}
                          </span>
                          {fighter.FighterId === lotn && (
                            <span className="text-xs font-bold bg-gold text-dark-grey px-2 py-0.5 rounded-full">
                              LOTN
                            </span>
                          )}
                          {fighter.FighterId === alternateLotn && (
                            <span className="text-xs font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                              Alt LOTN
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-medium-grey">
                          {getWeightClassForFighter(fighter.FighterId)}
                        </span>
                      </div>
                    ),
                )}
              </div>
            </div>

            {/* Alternates Summary */}
            {alternates.length > 0 && (
              <div className="mb-4">
                <h3 className="font-bold text-sm text-dark-grey mb-2">
                  Alternates
                </h3>
                <div className="space-y-2">
                  {alternates.map((fighter, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-medium-grey bg-gray-200 w-6 h-6 rounded-full flex items-center justify-center font-bold">
                          A{index + 1}
                        </span>
                        <span className="font-medium text-sm text-dark-grey">
                          {fighter.FirstName} {fighter.LastName}
                        </span>
                        {fighter.FighterId === alternateLotn && (
                          <span className="text-xs font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                            Alt LOTN
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-medium-grey">
                        {getWeightClassForFighter(fighter.FighterId)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Edit Button */}
            <button
              onClick={handleEditLineup}
              className="w-full bg-deep-red text-white py-3 rounded-xl text-sm font-bold hover:bg-bright-red transition-colors"
            >
              Edit Lineup
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-24">
      {/* Countdown Timer */}
      {eventStart && fighters && (
        <div className="flex justify-center pt-4">
          <CountdownTimer
            targetDate={eventStart}
            eventName={fighters.Name || fighters.name}
            showIcon={true}
          />
        </div>
      )}

      {/* Primary Lineup Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="border-b border-gray-100 p-3">
          <h3 className="font-bold text-sm text-dark-grey">Primary Lineup</h3>
          <p className="text-xs text-medium-grey">Select 5 fighters + LOTN</p>
        </div>
        <div className="p-4">
          <LineupSlots
            lineup={lineup}
            lotn={lotn}
            alternateLotn={alternateLotn}
            onAddFighter={handleOpenSelection}
            onRemoveFighter={removeFighter}
            onMoveFighter={moveFighter}
            onToggleLotn={toggleLotn}
            onToggleAlternateLotn={toggleAlternateLotn}
            eventData={getEventDataForModal()}
          />
        </div>
      </div>

      {/* Alternates Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="border-b border-gray-100 p-3">
          <h3 className="font-bold text-sm text-dark-grey">Alternates</h3>
          <p className="text-xs text-medium-grey">Select 2 alternates</p>
        </div>
        <div className="p-4">
          <AlternatePicks
            alternates={alternates}
            onAddAlternate={() => handleOpenSelection(null, true)}
            onRemoveAlternate={removeAlternate}
            isDisabled={!lineup.every((fighter) => fighter !== null)}
            eventData={getEventDataForModal()}
          />
        </div>
      </div>

      {/* Remove All Section */}
      {(lineup.some((f) => f) || alternates.length > 0) && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4">
            <button
              onClick={handleRemoveAllClick}
              className="w-full bg-red-600 hover:bg-red-700 text-white py-3 px-4 rounded-xl text-sm font-bold transition-colors flex items-center justify-center gap-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              Remove All Selections
            </button>
            <p className="text-xs text-medium-grey text-center mt-2">
              Clear your entire lineup and start over
            </p>
          </div>
        </div>
      )}

      {/* Footer-style Summary */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-4xl mx-auto p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-deep-red">
                  {projectedTotal?.toFixed(1) || "0.0"}
                </div>
                <div className="text-xs text-medium-grey">Projected Points</div>
              </div>
              <div className="text-sm text-medium-grey">
                <div>Lineup: {lineup.filter((f) => f).length}/5</div>
                <div>Alternates: {alternates.length}/2</div>
              </div>
            </div>

            <button
              onClick={handleSubmitButtonClick}
              disabled={!isComplete}
              className={`px-6 py-3 rounded-lg text-sm font-bold transition-colors ${
                isComplete
                  ? "bg-deep-red text-white hover:bg-bright-red"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              Submit Lineup
            </button>
          </div>
        </div>
      </div>

      {/* Fighter Selection Modal */}
      {showSelectionModal && (
        <FighterSelectionModal
          position={currentPosition}
          isAlternatePick={isAlternatePick}
          onClose={() => setShowSelectionModal(false)}
          onSelect={handleSelectFighter}
          onRemoveFromSlot={(index) => removeFighter(index)}
          onSwitchSlot={(index) => setCurrentPosition(index)}
          allFighters={getEventDataForModal()}
          lineup={lineup}
          alternates={alternates}
          lotn={lotn}
          alternateLotn={alternateLotn}
          isFighterOrOpponentInLineup={(index) => {
            const flatFighters = getFlatFightersArray();
            return isFighterOrOpponentInLineup(index, flatFighters);
          }}
          eventStart={eventStart}
        />
      )}

      {/* Submission Confirmation Modal */}
      {showSubmissionModal && (
        <SubmissionModal
          isOpen={showSubmissionModal}
          onClose={() => setShowSubmissionModal(false)}
          onConfirm={handleConfirmSubmit}
          lineup={lineup}
          lotn={lotn}
          alternates={alternates}
          alternateLotn={alternateLotn}
          projectedTotal={projectedTotal}
          getWeightClass={getWeightClassForFighter}
        />
      )}

      {/* Global Lineup Sync Modal */}
      <GlobalLineupSyncModal
        isOpen={showGlobalSyncModal}
        onClose={handleGlobalSyncDecline}
        onConfirm={handleGlobalSyncConfirm}
        isSubmitting={isLoading}
        isUpdate={isGlobalSyncUpdate}
      />

      {/* Remove All Confirmation Modal */}
      <RemoveAllConfirmationModal
        isOpen={showRemoveAllConfirmation}
        onClose={handleCancelRemoveAll}
        onConfirm={handleConfirmRemoveAll}
        lineup={lineup}
        alternates={alternates}
      />

      {/* Swap Confirmation Modal */}
      {swapConfirmation && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={handleCancelSwap}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-auto overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-deep-red to-bright-red p-4 text-white">
              <h3 className="text-lg font-bold">Swap Fighter?</h3>
              <p className="text-white/80 text-sm mt-0.5">
                Slot {swapConfirmation.targetSlot + 1} is already filled
              </p>
            </div>

            {/* Content */}
            <div className="p-4">
              <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-100 rounded-xl mb-3">
                <div className="w-8 h-8 bg-deep-red text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                  {swapConfirmation.targetSlot + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-medium-grey">
                    Currently in slot
                  </div>
                  <div className="font-semibold text-sm text-dark-grey truncate">
                    {swapConfirmation.existingFighter.FirstName}{" "}
                    {swapConfirmation.existingFighter.LastName}
                  </div>
                </div>
              </div>

              <div className="flex justify-center my-2">
                <div className="text-xs text-medium-grey font-medium bg-gray-100 px-3 py-1 rounded-full">
                  will be replaced by
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-100 rounded-xl">
                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                  {swapConfirmation.targetSlot + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-medium-grey">New fighter</div>
                  <div className="font-semibold text-sm text-dark-grey truncate">
                    {swapConfirmation.newFighter.FirstName}{" "}
                    {swapConfirmation.newFighter.LastName}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="p-4 bg-gray-50 border-t border-gray-100 flex gap-3">
              <button
                onClick={handleCancelSwap}
                className="flex-1 py-3 px-4 bg-white border border-gray-200 text-dark-grey font-medium rounded-xl hover:bg-gray-50 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSwap}
                className="flex-1 py-3 px-4 bg-deep-red text-white font-bold rounded-xl hover:bg-bright-red transition-colors text-sm"
              >
                Yes, Swap
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LineupSubmission;
